import { useEffect } from 'react';
import { Bell, Webhook, Landmark, Target, ArrowRight, CheckCircle2 } from 'lucide-react';
import HowItWorksStepper from './HowItWorksStepper';
import WebinarRegistrationForm from './WebinarRegistrationForm';
import FaqAccordion from './FaqAccordion';
import { WEBINAR_CONFIG, DEMO_WHATSAPP_URL } from './webinarConfig';

const META_TITLE = 'TradingView Auto Trading Webinar (Free) | AlgoSys';
const META_DESCRIPTION =
  'Free live webinar: turn TradingView alerts into real broker orders with auto Target, SL & Trailing SL. FYERS, Zerodha, MasterTrust. Register your seat.';

function useDocumentMeta(title, description) {
  useEffect(() => {
    if (typeof document === 'undefined') return undefined;
    const previousTitle = document.title;
    document.title = title;

    let meta = document.querySelector('meta[name="description"]');
    const metaExisted = !!meta;
    if (!meta) {
      meta = document.createElement('meta');
      meta.setAttribute('name', 'description');
      document.head.appendChild(meta);
    }
    const previousContent = meta.getAttribute('content');
    meta.setAttribute('content', description);

    return () => {
      document.title = previousTitle;
      if (metaExisted) {
        if (previousContent !== null) meta.setAttribute('content', previousContent);
        else meta.removeAttribute('content');
      } else {
        meta.remove();
      }
    };
  }, [title, description]);
}

function Eyebrow({ children }) {
  return (
    <div className="badge border border-amber/30 bg-amber/10 text-amber">{children}</div>
  );
}

function SectionHeading({ eyebrow, title, className = '' }) {
  return (
    <div className={className}>
      {eyebrow && <div className="section-title text-amber">{eyebrow}</div>}
      <h2 className="auth-font-heading mt-2 text-2xl font-bold text-txt-primary md:text-3xl">{title}</h2>
    </div>
  );
}

function HeroFlow() {
  const nodes = [
    { icon: Bell, label: 'TradingView Alert' },
    { icon: Webhook, label: 'AlgoSys Webhook' },
    { icon: Landmark, label: 'Broker Order' },
    { icon: Target, label: 'Target / SL / TSL' },
  ];
  return (
    <div className="card p-6 md:p-8">
      <div className="flex flex-col gap-4 md:flex-row md:items-center md:gap-2">
        {nodes.map((node, i) => (
          <div key={node.label} className="flex flex-1 items-center gap-4 md:flex-col md:gap-3 md:text-center">
            <div className="flex h-12 w-12 flex-none items-center justify-center rounded-full border border-amber/30 bg-amber/10">
              <node.icon size={20} className="text-amber" />
            </div>
            <span className="text-sm font-semibold text-txt-primary">{node.label}</span>
            {i < nodes.length - 1 && (
              <ArrowRight size={18} className="ml-auto flex-none text-txt-muted md:mx-auto md:my-1 md:rotate-90 md:ml-0" />
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

const ABOVE_FOLD_BULLETS = [
  'Works with Pine Script strategies and indicator alerts',
  'Executes through your own broker — NSE, BSE, MCX',
  'Target, SL, and Trailing SL managed automatically after entry',
  'Pause, exit, or kill any position from one dashboard',
  'No coding beyond the TradingView alert you already have',
];

const WEBINAR_SEE_ITEMS = [
  'How a TradingView alert becomes a live broker order in real time',
  'Setting Target, Stop Loss, and Trailing Stop Loss rules before the trade',
  'A live look at the AlgoSys dashboard — P/L, orderbook, positions',
  'Pause / exit / kill controls in action',
  'Live Q&A with the AlgoSys team',
];

const FEATURES = [
  {
    title: 'Webhook-native integration',
    desc: 'Strategy order fills or indicator conditions — if a TradingView alert can send a webhook, AlgoSys can trade it.',
  },
  {
    title: 'Broker-connected execution',
    desc: 'Orders go through your own account at FYERS, Zerodha, or MasterTrust, with workspace pages for funds, orderbook, positions, and holdings — verify everything against what your broker shows.',
  },
  {
    title: 'Full segment coverage',
    desc: 'NSE, BSE, and MCX — Futures, Index Options, and dynamic option strategies. One signal can drive an option position with dynamic strike logic.',
  },
  {
    title: 'Post-entry risk management',
    desc: 'Every trade can carry a Target, Stop Loss, and Trailing Stop Loss, monitored continuously and executed automatically.',
  },
  {
    title: 'Live P/L and observability',
    desc: 'Real-time P/L per position and per strategy. No alt-tabbing between TradingView, the broker terminal, and a spreadsheet.',
  },
  {
    title: 'Manual override, always',
    desc: 'Pause new entries, exit one position, or kill everything. Automation never locks you out of your own trades.',
  },
];

const USE_CASES = [
  {
    title: 'Pine Script strategy automation',
    desc: 'Your backtested strategy trades live through your broker — same logic, real orders, disciplined exits.',
  },
  {
    title: 'Indicator alert automation',
    desc: 'No full strategy script needed. A supertrend flip, an RSI cross, a price level — any indicator alert can become a live, risk-managed trade.',
  },
  {
    title: 'Options trading automation',
    desc: 'A NIFTY chart signal can execute an index option position, including dynamic option strategies, managed as one unit with Target, SL, and TSL on the position.',
  },
  {
    title: 'Semi-automated workflow',
    desc: 'Prefer confirming entries yourself? Take the entry manually from the AlgoSys dashboard and let the platform manage the exit with your predefined rules. Your judgment in, system discipline out.',
  },
];

function DemoLink({ className = '' }) {
  return (
    <a
      href={DEMO_WHATSAPP_URL}
      target="_blank"
      rel="noreferrer"
      className={`text-sm font-semibold text-amber underline underline-offset-2 ${className}`}
    >
      Prefer a 1-on-1 walkthrough? Book a free setup demo.
    </a>
  );
}

export default function TradingViewLandingPage() {
  useDocumentMeta(META_TITLE, META_DESCRIPTION);

  useEffect(() => {
    document.documentElement.setAttribute('data-theme', 'dark');
    document.documentElement.classList.add('scroll-smooth');
    return () => {
      document.documentElement.classList.remove('scroll-smooth');
    };
  }, []);

  return (
    <main className="auth-font-body relative bg-bg-base text-txt-primary">
      <div className="pointer-events-none fixed inset-0 overflow-hidden">
        <div className="absolute -top-32 left-1/2 h-[500px] w-[700px] -translate-x-1/2 rounded-full bg-amber/[0.06] blur-[120px]" />
        <div className="absolute -bottom-32 -right-32 h-[500px] w-[500px] rounded-full bg-info/[0.05] blur-[100px]" />
      </div>

      <div className="relative z-10 mx-auto max-w-5xl px-4 py-16 md:py-24">
        {/* HERO */}
        <section className="grid gap-10 md:grid-cols-2 md:items-center md:gap-14">
          <div>
            <Eyebrow>Free Live Webinar · {WEBINAR_CONFIG.campaignTitle}</Eyebrow>
            <h1 className="auth-font-heading mt-4 text-3xl font-bold leading-tight text-txt-primary md:text-5xl">
              Your TradingView Alert Just Fired. Did Your Order?
            </h1>
            <p className="mt-5 text-base leading-relaxed text-txt-secondary md:text-lg">
              Join our free live webinar and watch a TradingView alert become a real broker
              order — with Target, Stop Loss, and Trailing Stop Loss managed automatically. See
              the full workflow on FYERS, Zerodha, or MasterTrust before you connect anything.
            </p>
            <div className="mt-8 flex flex-wrap items-center gap-4">
              <a href="#registration-form" className="btn-primary">Register for Free Webinar</a>
              <a href="#how-it-works" className="btn-outline">See How It Works</a>
            </div>
            <div className="mt-4">
              <DemoLink />
            </div>
          </div>
          <HeroFlow />
        </section>

        {/* ABOVE-THE-FOLD BULLETS */}
        <section className="mt-14 grid gap-3 sm:grid-cols-2">
          {ABOVE_FOLD_BULLETS.map((bullet) => (
            <div key={bullet} className="flex items-start gap-2.5">
              <CheckCircle2 size={18} className="mt-0.5 flex-none text-amber" />
              <span className="text-sm text-txt-secondary">{bullet}</span>
            </div>
          ))}
        </section>

        {/* WHAT YOU'LL SEE IN THE WEBINAR */}
        <section className="mt-24">
          <SectionHeading eyebrow="This live session" title="What you'll see in the webinar" />
          <div className="mt-6 grid gap-6 md:grid-cols-[1.1fr_0.9fr]">
            <div className="card p-6 md:p-8">
              <ul className="space-y-3.5">
                {WEBINAR_SEE_ITEMS.map((item) => (
                  <li key={item} className="flex items-start gap-2.5">
                    <CheckCircle2 size={18} className="mt-0.5 flex-none text-amber" />
                    <span className="text-sm text-txt-secondary">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="card flex flex-col justify-center gap-3 p-6 md:p-8">
              <div className="text-sm text-txt-secondary">
                <span className="font-semibold text-txt-primary">{WEBINAR_CONFIG.durationMinutes}-minute live session</span>
                {' · '}Hosted by {WEBINAR_CONFIG.presenter}
              </div>
              <div className="text-sm text-txt-secondary">{WEBINAR_CONFIG.platformNote}</div>
              <div className="mt-2 rounded-md border border-amber/25 bg-amber/10 px-3 py-2 text-sm font-semibold text-amber">
                Recording will not be shared publicly, so attend live.
              </div>
            </div>
          </div>
        </section>

        {/* PROBLEM */}
        <section className="mt-24 max-w-3xl">
          <SectionHeading title="TradingView alerts are powerful. Alerts are not execution." />
          <div className="mt-5 space-y-4 text-base leading-relaxed text-txt-secondary">
            <p>
              Your Pine Script does its job. The alert fires on exactly the right candle. Then
              everything depends on you — and you&apos;re in a meeting, watching another chart, or
              two minutes late to the notification. By the time you type the order, the entry is
              gone or you&apos;re chasing it.
            </p>
            <p>
              And entry is only half the job. Someone still has to place the stop, trail it, book
              the target, and flatten before a reversal. On a busy expiry day, &quot;someone&quot;
              is a stressed human juggling five windows.
            </p>
            <p>The strategy was never the weak link. The gap between alert and order is.</p>
          </div>
        </section>

        {/* SOLUTION */}
        <section className="mt-24 max-w-3xl">
          <SectionHeading title="AlgoSys turns alerts into managed trades." />
          <div className="mt-5 space-y-4 text-base leading-relaxed text-txt-secondary">
            <p>
              AlgoSys is the execution layer between TradingView and your broker. Add one webhook
              URL to your alert. When it fires, AlgoSys receives it in real time, places the order
              at your connected broker, and takes over trade management — Target, Stop Loss, and
              Trailing Stop Loss, by rules you set in advance.
            </p>
            <p>
              Your strategy stays in TradingView, exactly as you built it. Your funds stay with
              your broker. AlgoSys handles the part in between — with live P/L and controls to
              pause, exit, or kill whenever you decide.
            </p>
            <p className="font-semibold text-txt-primary">Automation with your hands still on the wheel.</p>
          </div>
        </section>

        {/* HOW IT WORKS */}
        <section id="how-it-works" className="mt-24 scroll-mt-8">
          <SectionHeading eyebrow="How it works" title="How TradingView auto trading works with AlgoSys" />
          <div className="mt-8">
            <HowItWorksStepper />
          </div>
        </section>

        {/* FEATURES */}
        <section className="mt-24">
          <SectionHeading title="Built for TradingView traders" />
          <div className="mt-8 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {FEATURES.map((f) => (
              <div key={f.title} className="card p-6">
                <h3 className="font-display text-base font-bold text-txt-primary">{f.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-txt-secondary">{f.desc}</p>
              </div>
            ))}
          </div>
        </section>

        {/* USE CASES */}
        <section className="mt-24">
          <SectionHeading title="Use cases" />
          <div className="mt-8 grid gap-5 sm:grid-cols-2">
            {USE_CASES.map((u) => (
              <div key={u.title} className="card p-6">
                <h3 className="font-display text-base font-bold text-txt-primary">{u.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-txt-secondary">{u.desc}</p>
              </div>
            ))}
          </div>
        </section>

        {/* RISK AND CONTROL */}
        <section className="mt-24 max-w-3xl">
          <SectionHeading title="Automation without losing the wheel" />
          <div className="mt-5 space-y-4 text-base leading-relaxed text-txt-secondary">
            <p>
              The biggest fear about automation is the right one: what if the system does
              something I can&apos;t see or can&apos;t stop?
            </p>
            <p>
              AlgoSys is built around that fear. Every position shows live P/L. Every order
              appears in an orderbook you can check against your broker. And three controls are
              always available: <span className="font-semibold text-txt-primary">pause</span> a
              strategy, <span className="font-semibold text-txt-primary">exit</span> a position, or{' '}
              <span className="font-semibold text-txt-primary">kill</span> everything at once.
            </p>
            <p>
              One honest note: exits are placed as real orders with your broker. In fast markets
              or opening gaps, any stop loss can slip — automated or manual. AlgoSys removes
              hesitation, forgotten stops, and missed alerts. It doesn&apos;t change how markets
              work, and you should be suspicious of any platform that says otherwise.
            </p>
          </div>
        </section>

        {/* TRUST */}
        <section className="mt-24 max-w-3xl">
          <SectionHeading title="What AlgoSys is — and what it isn't" />
          <p className="mt-5 text-base leading-relaxed text-txt-secondary">
            AlgoSys is a technology platform. It executes and manages the strategies{' '}
            <span className="font-semibold text-txt-primary">you</span> choose to run. It does not
            give trading tips, recommend trades, manage your portfolio, or promise returns. Your
            funds stay with your broker at all times — AlgoSys never holds client money.
          </p>
        </section>

        {/* REGISTRATION FORM */}
        <section id="registration-form" className="mt-24 scroll-mt-8">
          <div className="mx-auto max-w-lg text-center">
            <SectionHeading title="Register for the free webinar" />
          </div>
          <div className="mx-auto mt-8 max-w-lg">
            <WebinarRegistrationForm />
            <div className="mt-4 text-center">
              <DemoLink />
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section className="mt-24">
          <SectionHeading eyebrow="FAQ" title="TradingView automation — common questions" />
          <div className="mx-auto mt-8 max-w-2xl">
            <FaqAccordion />
          </div>
        </section>

        {/* FINAL CTA */}
        <section className="mt-24">
          <div className="card relative overflow-hidden p-8 text-center md:p-12">
            <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-amber/50 to-transparent" />
            <h2 className="auth-font-heading text-2xl font-bold text-txt-primary md:text-3xl">
              Your strategy already works on the chart. Let it work in the market.
            </h2>
            <p className="mx-auto mt-4 max-w-xl text-base leading-relaxed text-txt-secondary">
              Register for the free webinar. We&apos;ll walk through connecting a broker, wiring
              up a TradingView alert, and the risk controls — live.
            </p>
            <a href="#registration-form" className="btn-primary mt-8 inline-flex">Register for Free Webinar</a>
            <p className="mx-auto mt-4 max-w-md text-xs text-txt-muted">
              Bring one TradingView alert or Pine Script strategy you want to automate — we&apos;ll
              walk through the exact setup live.
            </p>
          </div>
        </section>

        {/* FOOTER DISCLAIMER */}
        <footer className="mt-20 border-t border-bg-border/60 pt-8 text-center">
          <p className="mx-auto max-w-2xl text-xs leading-relaxed text-txt-muted">
            AlgoSys is a technology platform for trading automation. It does not provide
            investment advice, trading tips, portfolio management, or guaranteed returns. Trading
            involves market risk.
          </p>
          <p className="mt-4 text-xs text-txt-muted">© {new Date().getFullYear()} AlgoSys</p>
        </footer>
      </div>
    </main>
  );
}
